import { IServerConnection, Server, IRetryPolicySettings } from '@/app-modules/server-communications';
import { IServerServiceStreamProperties, IChatServerServiceStreamProperties, IGameServerServiceStreamProperties, IJsonGameServerServiceStreamProperties } from '@/app-modules/server-service/server-service-stream-properties';
import { ChatModsServer } from '@/app-modules/server-service/chatmods-server';
import { CatchUpData, WinnersData, ResetData, CallsData, NextGameData, BoxNumbersData } from '@/components/game/_domain';
import {  JsonCallData, JsonCountdownData, JsonWinnersData, JsonWelcomeData, JsonStartData } from '@/components/game/_domain/server/json/prize/json-prize-data';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';
import { IServerResponseStreamsResolver } from '@/app-modules/server-service/server-service-stream-resolver';
import { HamsterRaceInfoData } from '@/components/game/_domain/server/hamster-race-info/hamster-race-info-data';
import { WinnerCatchupData } from '@/components/game/_domain/server/winner-catchup/winner-catchup-data';
import { SetStageData } from '@/components/game/_domain/server/set-stage';
import { ModifyUserMessageResponse, UserSearchResultResponse, ArcadeWinnerMessageResponse,
         JackpotWinnerMessageResponse, ChatPrizeResponse, HistoryResponse, MessageResponse,
         RemoveUserResponse, AddUserResponse, UserListResponse } from '@/components/chat/_domain';
import { ISubscription } from 'rxjs/Subscription';
import { IAuthenticationResponse } from '@/app-modules/server-service/auth-response';

interface IServerServiceConfiguration {
  connection: IServerConnection;
  createStreams: IServerResponseStreamsResolver;
  retryPolicySettings?: IRetryPolicySettings;
}

interface IBaseServerService {
  subscribeConnecting(func: () => void): ISubscription;
  subscribeConnected(func: () => void): ISubscription;
  subscribeConnectionLost(func: () => void): ISubscription;
  subscribeDisconnecting(func: () => void): ISubscription;
  subscribeDisconnected(func: () => void): ISubscription;
  subscribeRetryCircuitBroken(func: () => void): ISubscription;
  subscribeAuth(func: (payload: IAuthenticationResponse) => void): ISubscription;
  connect(func?: () => void): IBaseServerService;
  disconnect(force: boolean): IBaseServerService;
  send(command: any): IBaseServerService;
  sendOfflineMessageBuffer(): void;
  readonly retryPolicySettings: IRetryPolicySettings;
}

class BaseServerService<T extends IServerServiceStreamProperties> implements IBaseServerService {
  protected server: Nullable<Server<T>> = null;
  protected retryPolicySettingsObject: IRetryPolicySettings = null;

  public subscribeConnecting(func: () => void): ISubscription {
    return this.server.streams.connectionStatusConnecting$.subscribe(func);
  }

  public subscribeConnected(func: () => void): ISubscription {
    return this.server.streams.connectionStatusConnected$.subscribe(func);
  }

  public subscribeConnectionLost(func: () => void): ISubscription {
    return this.server.streams.connectionStatusConnectionLost$.subscribe(func);
  }

  public subscribeDisconnecting(func: () => void): ISubscription {
    return this.server.streams.connectionStatusDisconnecting$.subscribe(func);
  }

  public subscribeDisconnected(func: () => void): ISubscription {
    return this.server.streams.connectionStatusDisconnected$.subscribe(func);
  }

  public subscribeRetryCircuitBroken(func: () => void): ISubscription {
    return this.server.streams.connectionStatusRetryCircuitBroken$.subscribe(func);
  }

  public subscribeAuth(func: (payload: IAuthenticationResponse) => void): ISubscription {
    return this.server.streams.auth$.subscribe(func);
  }

  public connect(func?: () => void): BaseServerService<T> {
    this.server.connect().then(() => {
      if (func) {
        func();
      }
    }).catch((reason) => {
      const errorMessage = this.getErrorMessage(reason);
      throw new Error(errorMessage);
    });
    return this;
  }

  public disconnect(force: boolean): BaseServerService<T> {
    const promise = this.server.disconnect(force);

    if (promise) {
      promise.catch((reason) => {
        const errorMessage = this.getErrorMessage(reason);
        throw new Error(errorMessage);
      });
    }

    return this;
  }

  public send(command: any): BaseServerService<T> {
    this.server.send(command);
    return this;
  }

  public sendOfflineMessageBuffer(): void {
    this.server.sendOfflineMessageBuffer();
  }

  public get retryPolicySettings(): IRetryPolicySettings {
    return this.retryPolicySettingsObject;
  }

  private getErrorMessage(reason: any): string {
    return reason ? JSON.stringify(reason) : 'internal server error';
  }

  constructor(configuration: IServerServiceConfiguration) {
    this.retryPolicySettingsObject = configuration.retryPolicySettings;
    this.server = new ChatModsServer(configuration.connection, configuration.createStreams, configuration.retryPolicySettings);
  }
}

interface IChatServerService extends IBaseServerService {
  subscribeUserList(func: (payload: UserListResponse) => void): ISubscription;
  subscribeAddUser(func: (payload: AddUserResponse) => void): ISubscription;
  subscribeRemoveUser(func: (payload: RemoveUserResponse) => void): ISubscription;
  subscribeMessage(func: (payload: MessageResponse) => void): ISubscription;
  subscribeHistory(func: (payload: HistoryResponse) => void): ISubscription;
  subscribeChatPrize(func: (payload: ChatPrizeResponse) => void): ISubscription;
  subscribeJackpotWinnerMessage(func: (payload: JackpotWinnerMessageResponse) => void): ISubscription;
  subscribeArcadeWinnerMessage(func: (payload: ArcadeWinnerMessageResponse) => void): ISubscription;
  subscribeUserSearchResult(func: (payload: UserSearchResultResponse) => void): ISubscription;
  subscribeModifyUserMessage(func: (payload: ModifyUserMessageResponse) => void): ISubscription;
}

class ChatServerService extends BaseServerService<IChatServerServiceStreamProperties> implements IChatServerService {
  public subscribeUserList(func: (payload: any) => void): ISubscription {
    return this.server.streams.userlist$.subscribe(func);
  }

  public subscribeAddUser(func: (payload: AddUserResponse) => void): ISubscription {
    return this.server.streams.adduser$.subscribe(func);
  }

  public subscribeRemoveUser(func: (payload: RemoveUserResponse) => void): ISubscription {
    return this.server.streams.removeuser$.subscribe(func);
  }

  public subscribeMessage(func: (payload: MessageResponse) => void): ISubscription {
    return this.server.streams.message$.subscribe(func);
  }

  public subscribeHistory(func: (payload: HistoryResponse) => void): ISubscription {
    return this.server.streams.history$.subscribe(func);
  }

  public subscribeChatPrize(func: (payload: ChatPrizeResponse) => void): ISubscription {
    return this.server.streams.chatPrize$.subscribe(func);
  }

  public subscribeJackpotWinnerMessage(func: (payload: JackpotWinnerMessageResponse) => void): ISubscription {
    return this.server.streams.jackpotMessage$.subscribe(func);
  }

  public subscribeArcadeWinnerMessage(func: (payload: ArcadeWinnerMessageResponse) => void): ISubscription {
    return this.server.streams.arcadeWinner$.subscribe(func);
  }

  public subscribeUserSearchResult(func: (payload: UserSearchResultResponse) => void): ISubscription {
    return this.server.streams.userSearch$.subscribe(func);
  }

  public subscribeModifyUserMessage(func: (payload: ModifyUserMessageResponse) => void): ISubscription {
    return this.server.streams.modifyUser$.subscribe(func);
  }
}

interface IGameServerService extends IBaseServerService {
  subscribeCalls(func: (payload: CallsData) => void): ISubscription;
  subscribeWinners(func: (payload: WinnersData) => void): ISubscription;
  subscribeCatchUp(func: (payload: CatchUpData) => void): ISubscription;
  subscribeReset(func: (payload: ResetData) => void): ISubscription;
  subscribeNextGame(func: (payload: NextGameData) => void): ISubscription;
  subscribePrizeWinner(func: (payload: PrizeWinnerData) => void): ISubscription;
  subscribeHamsterRaceInfo(func: (payload: HamsterRaceInfoData) => void): ISubscription;
  subscribeWinnerCatchup(func: (payload: WinnerCatchupData) => void): ISubscription;
  subscribeSetStage(func: (payload: SetStageData) => void): ISubscription;
  subscribeBoxNumbers(func: (payload: BoxNumbersData) => void): ISubscription;
}

interface IJsonGameServerService extends IBaseServerService {
  subscribeCalls(func: (payload: JsonCallData) => void): ISubscription;
  subscribeWinners(func: (payload: JsonWinnersData) => void): ISubscription;
  subscribeWelcome(func: (payload: JsonWelcomeData) => void): ISubscription;
  subscribeStart(func: (payload: JsonStartData) => void): ISubscription;
  subscribeCountdown(func: (payload: JsonCountdownData) => void): ISubscription;
}

interface IGameServerServiceConfiguration extends IServerServiceConfiguration {
  gameName: string;
}

class JsonGameServerService extends BaseServerService<IJsonGameServerServiceStreamProperties> implements IJsonGameServerService{
  constructor(configuration: IGameServerServiceConfiguration) {
    super(configuration);
  }
  public subscribeCalls(func: (payload: JsonCallData) => void): ISubscription {
    return this.server.streams.calls$.subscribe(func);
  }
  public subscribeWinners(func: (payload: JsonWinnersData) => void): ISubscription {
    return this.server.streams.winners$.subscribe(func);
  }
  public subscribeWelcome(func: (payload: JsonWelcomeData) => void): ISubscription {
    return this.server.streams.welcome$.subscribe(func);
  }
  public subscribeStart(func: (payload: JsonStartData) => void): ISubscription {
    return this.server.streams.start$.subscribe(func);
  }
  public subscribeCountdown(func: (payload: JsonCountdownData) => void): ISubscription {
    return this.server.streams.countdown$.subscribe(func);
  }
}

class GameServerService extends BaseServerService<IGameServerServiceStreamProperties> implements IGameServerService {
  constructor(configuration: IGameServerServiceConfiguration) {
    super(configuration);
  }

  public subscribeCalls(func: (payload: CallsData) => void): ISubscription {
    return this.server.streams.calls$.subscribe(func);
  }

  public subscribeWinners(func: (payload: WinnersData) => void): ISubscription {
    return this.server.streams.winners$.subscribe(func);
  }

  public subscribeCatchUp(func: (payload: CatchUpData) => void): ISubscription {
    return this.server.streams.catchUp$.subscribe(func);
  }

  public subscribeReset(func: (payload: ResetData) => void): ISubscription {
    return this.server.streams.reset$.subscribe(func);
  }

  public subscribeNextGame(func: (payload: NextGameData) => void): ISubscription {
    return this.server.streams.nextGame$.subscribe(func);
  }

  public subscribePrizeWinner(func: (payload: PrizeWinnerData) => void): ISubscription {
    return this.server.streams.prizeWinner$.subscribe(func);
  }

  public subscribeHamsterRaceInfo(func: (payload: HamsterRaceInfoData) => void): ISubscription {
    return this.server.streams.hamsterRaceInfo$.subscribe(func);
  }

  public subscribeWinnerCatchup(func: (payload: WinnerCatchupData) => void): ISubscription {
    return this.server.streams.winnerCatchUp$.subscribe(func);
  }

  public subscribeSetStage(func: (payload: SetStageData) => void): ISubscription {
    return this.server.streams.setStage$.subscribe(func);
  }

  public subscribeBoxNumbers(func: (payload: BoxNumbersData) => void): ISubscription{
    return this.server.streams.boxNumbers$.subscribe(func);
  }
}

export { GameServerService, IGameServerService, IGameServerServiceConfiguration,
         JsonGameServerService, IJsonGameServerService,
         ChatServerService, IChatServerService,
         IBaseServerService, IServerServiceConfiguration };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/server-service/server-service.ts